import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Globe2, Link2, Loader2, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { useDrive } from "@/context/DriveContext";
import { translateError } from "@/i18n/errors";

const STATUS = { ok: "drive.shareOk", already: "drive.shareAlready", error: "bulk.error" };

const FileRow = ({ f, result, busy, onToggle }) => {
  const { t, fmtBytes } = useI18n();
  return (
    <li className="flex min-w-0 items-center gap-3 rounded-xl border border-[rgba(245,197,66,0.18)] bg-black/30 px-3 py-2" data-testid={`drive-file-${f.id}`}>
      {f.public ? <Globe2 className="h-4 w-4 shrink-0 text-emerald-300" /> : <Lock className="h-4 w-4 shrink-0 text-neutral-500" />}
      <div className="min-w-0 flex-1">
        <p dir="ltr" className="truncate text-sm text-white">{f.name}</p>
        <p className="text-[0.7rem] text-neutral-500">
          {fmtBytes(f.size)} · <span data-testid={`drive-file-state-${f.id}`}>{t(f.public ? "drive.public" : "drive.private")}</span>
          {result && <span className={result.status === "error" ? " text-red-300" : " text-emerald-300"} data-testid={`drive-file-result-${f.id}`}> · {t(STATUS[result.status])}{result.detail ? ` (${result.detail})` : ""}</span>}
        </p>
      </div>
      <Switch checked={f.public} disabled={busy || !f.owned} onCheckedChange={onToggle} aria-label={f.name} data-testid={`drive-file-toggle-${f.id}`} />
    </li>
  );
};

// "Rendre mes APK Drive accessibles par lien" for the connected account of this space.
export const DriveSharePanel = () => {
  const { t } = useI18n();
  const { base } = useSpace();
  const drive = useDrive();
  const cfg = drive?.cfg;
  const [files, setFiles] = useState(null);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${base}/drive/files`);
      setFiles(data.files);
      if (data.auto.length) {
        setResults(Object.fromEntries(data.auto.map((r) => [r.id, r])));
        toast.success(t("drive.autoApplied", { n: data.auto.filter((r) => r.status === "ok").length }));
      }
    } catch (err) {
      setFiles([]);
      toast.error(translateError(err, t));
      drive.refresh();
    }
  }, [base, t, drive]);
  useEffect(() => { if (cfg?.connected) load(); }, [cfg?.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  const share = async (ids, pub) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/drive/share`, { file_ids: ids, public: pub });
      setResults((r) => ({ ...r, ...Object.fromEntries(data.results.map((x) => [x.id, x])) }));
      const ok = new Set(data.results.filter((x) => x.status !== "error").map((x) => x.id));
      setFiles((fs) => fs.map((f) => (ok.has(f.id) ? { ...f, public: pub } : f)));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const setAuto = async (on) => {
    try {
      await api.put(`${base}/drive/settings`, { auto_share: on });
      drive.refresh();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };

  if (!cfg) return null;
  const privates = (files || []).filter((f) => !f.public && f.owned).map((f) => f.id);
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="drive-share-panel">
      <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><Link2 className="h-5 w-5 text-[var(--gold)]" /> {t("drive.shareTitle")}</h3>
      <p className="mt-1 text-xs text-neutral-400">{t("drive.shareHint")}</p>
      <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/50 bg-amber-950/30 p-3 text-xs text-amber-100" data-testid="drive-share-warning">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("drive.shareWarning")}
      </p>
      {!cfg.enabled && <button type="button" className="btn-red btn-sm mt-4" disabled data-testid="drive-connect-btn">{t("drive.connect")}</button>}
      {!cfg.enabled && <p className="mt-1 text-xs text-neutral-500" data-testid="drive-share-soon">{t("drive.soon")}</p>}
      {cfg.enabled && !cfg.connected && (
        <button type="button" className="btn-gold btn-sm mt-4" onClick={drive.connect} data-testid="drive-connect-btn">{t("drive.connect")}</button>
      )}
      {cfg.connected && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-300">
            <span className="min-w-0 truncate" data-testid="drive-account-email">{t("drive.connectedAs", { email: cfg.email || "Google" })}</span>
            <button type="button" className="inline-flex items-center gap-1 text-red-300 underline" onClick={drive.disconnect} data-testid="drive-disconnect-btn"><LogOut className="h-3.5 w-3.5" /> {t("drive.disconnect")}</button>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm text-neutral-200">
            {t("drive.autoShare")}
            <Switch checked={!!cfg.auto_share} onCheckedChange={setAuto} data-testid="drive-auto-share-switch" />
          </label>
          <button type="button" className="btn-gold btn-sm" disabled={busy || !privates.length} onClick={() => share(privates, true)} data-testid="drive-share-all-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("drive.shareAll")} {privates.length ? `(${privates.length})` : ""}
          </button>
          {files === null && <Loader2 className="h-5 w-5 animate-spin text-[var(--gold)]" />}
          {files?.length === 0 && <p className="text-xs text-neutral-500" data-testid="drive-files-empty">{t("drive.noFiles")}</p>}
          <ul className="max-h-80 space-y-2 overflow-y-auto" data-testid="drive-files-list">
            {files?.map((f) => <FileRow key={f.id} f={f} result={results[f.id]} busy={busy} onToggle={(on) => share([f.id], on)} />)}
          </ul>
        </div>
      )}
    </section>
  );
};
